import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Edit as EditIcon, PersonAdd as SubmitIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { formatJobLocationLine } from '../../utils/locationDisplay';
import { useAuth } from '../../context/AuthContext';
import { DetailPageHeader, DetailTwoColumnGrid } from '../../components/common/DetailPageLayout';
import RecordAuditMeta from '../../components/common/RecordAuditMeta';

function ownerThru(o) {
  if (!o || typeof o !== 'object') return {};
  return o.job_requirement_owners || o.JobRequirementOwner || {};
}

export function JobDetailsPanel({ jobId, embedded = false, onClose }) {
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [submitDialog, setSubmitDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [submitForm, setSubmitForm] = useState({ contact_id: '', submitted_rate: '', internal_notes: '' });
  const [listingSaving, setListingSaving] = useState(false);
  const [primarySaving, setPrimarySaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    setJob(null);
    fetchJob();
    fetchContacts();
  }, [jobId]);

  const fetchJob = async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`/jobs/${jobId}`);
      setJob(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch job');
      if (embedded && typeof onClose === 'function') onClose();
      else navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try { const response = await api.get('/contacts?contact_type=candidate&status=active&limit=100'); setContacts(response.data.data); }
    catch (error) { console.error(error); }
  };

  const handleSubmitCandidate = async () => {
    try {
      await api.post(`/jobs/${jobId}/submissions`, submitForm);
      toast.success('Candidate submitted successfully');
      setSubmitDialog(false);
      setSubmitForm({ contact_id: '', submitted_rate: '', internal_notes: '' });
      fetchJob();
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to submit'); }
  };

  const handleTogglePublicListing = async () => {
    if (!hasPermission('jobs_modify') || !job) return;
    const isPublic = String(job.listing_visibility || '').toLowerCase() === 'public';
    const next = isPublic ? 'private' : 'public';
    setListingSaving(true);
    try {
      await api.put(`/jobs/${jobId}`, { listing_visibility: next });
      toast.success(
        next === 'public'
          ? 'Job will appear on /public when tenant Jobs visibility is Public.'
          : 'Job removed from the public portal.'
      );
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setListingSaving(false);
    }
  };

  const handleUpdateSubmission = async (submissionId, status) => {
    try {
      await api.put(`/jobs/${jobId}/submissions/${submissionId}`, { status });
      toast.success('Status updated');
      fetchJob();
    } catch (error) { toast.error('Failed to update'); }
  };

  const primaryContactId =
    job?.owners?.find((o) => ownerThru(o).assignment_role === 'primary')?.id || '';

  const handleSetPrimaryAssignee = async (contactId) => {
    if (!contactId || contactId === primaryContactId) return;
    setPrimarySaving(true);
    try {
      await api.put(`/jobs/${jobId}/primary-owner`, { contact_id: contactId });
      toast.success('Primary assignee updated.');
      fetchJob();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update primary assignee');
    } finally {
      setPrimarySaving(false);
    }
  };

  if (!jobId) return null;
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: embedded ? 6 : 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!job) return null;

  const priorityColors = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#10b981' };
  const statusColors = {
    active: '#10b981',
    on_hold: '#f59e0b',
    filled: '#6366f1',
    closed: '#64748b',
    cancelled: '#ef4444',
  };
  const jobInitial = (job.title || 'J').trim().charAt(0).toUpperCase();

  return (
    <Box sx={embedded ? { bgcolor: 'background.paper' } : undefined}>
      {embedded ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover'
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Job details
          </Typography>
          <IconButton size="small" aria-label="Close details" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <DetailPageHeader
          title="Job Details"
          onBack={() => navigate('/jobs')}
          actions={
            <>
              {hasPermission('jobs_add') && (
                <Button variant="outlined" startIcon={<SubmitIcon />} onClick={() => setSubmitDialog(true)}>
                  Submit Candidate
                </Button>
              )}
              {hasPermission('jobs_modify') && (
                <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/jobs/${jobId}/edit`)}>
                  Edit
                </Button>
              )}
            </>
          }
        />
      )}

      <DetailTwoColumnGrid
        sidebar={
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}>
                {jobInitial}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {job.title}
              </Typography>
              {job.job_code && (
                <Chip label={job.job_code} size="small" sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 600 }} />
              )}
              <Chip
                label={job.priority?.toUpperCase()}
                size="small"
                sx={{ mt: 1, bgcolor: `${priorityColors[job.priority]}20`, color: priorityColors[job.priority] }}
              />
              {job.status && (
                <Chip
                  label={job.status?.replace('_', ' ').toUpperCase()}
                  size="small"
                  sx={{
                    mt: 1,
                    ml: 1,
                    bgcolor: `${statusColors[job.status] || '#64748b'}20`,
                    color: statusColors[job.status] || '#64748b',
                  }}
                />
              )}
              {(job.status === 'closed' || job.status === 'cancelled') && job.closure_reason ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 640, display: 'block' }}>
                  <strong>Closure reason:</strong> {job.closure_reason}
                </Typography>
              ) : null}
              {hasPermission('jobs_modify') && (
                <Box sx={{ mt: 2, px: 1, py: 1.5, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'left' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={String(job.listing_visibility || '').toLowerCase() === 'public'}
                        onChange={handleTogglePublicListing}
                        disabled={listingSaving}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Public careers page</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4.5 }}>
                    Also enable Jobs under Administration → Visibility setup. Only active jobs with this on appear at{' '}
                    <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace' }}>
                      /public
                    </Typography>
                    .
                  </Typography>
                </Box>
              )}
              {embedded && hasPermission('jobs_add') && (
                <Box sx={{ mt: 2 }}>
                  <Button variant="outlined" size="small" startIcon={<SubmitIcon />} onClick={() => setSubmitDialog(true)}>
                    Submit Candidate
                  </Button>
                  {hasPermission('jobs_modify') && (
                    <Button variant="contained" size="small" sx={{ ml: 1 }} startIcon={<EditIcon />} onClick={() => navigate(`/jobs/${jobId}/edit`)}>
                      Edit
                    </Button>
                  )}
                </Box>
              )}
              <Divider sx={{ my: 3 }} />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {job.client?.company_name || 'N/A'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatJobLocationLine(job) || 'N/A'} ({job.remote_option})
                </Typography>
                {(job.address_line1 || job.city || job.state || job.zip_code) && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                      Address
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                      {[job.address_line1, job.address_line2].filter(Boolean).join('\n')}
                      {'\n'}
                      {[job.city, job.state, job.zip_code].filter(Boolean).join(', ')}
                      {job.country ? ` · ${job.country}` : ''}
                    </Typography>
                  </>
                )}
                <Typography variant="subtitle2" color="text.secondary">
                  Employment type
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {job.employment_type?.replace('_', ' ')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Rates
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  {job.bill_rate && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Bill
                      </Typography>
                      <Typography variant="body2">${job.bill_rate}/hr</Typography>
                    </Grid>
                  )}
                  {job.w2_rate && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        W2
                      </Typography>
                      <Typography variant="body2">${job.w2_rate}/hr</Typography>
                    </Grid>
                  )}
                  {job.c2c_rate && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        C2C
                      </Typography>
                      <Typography variant="body2">${job.c2c_rate}/hr</Typography>
                    </Grid>
                  )}
                  {job.rate_1099 && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        1099
                      </Typography>
                      <Typography variant="body2">${job.rate_1099}/hr</Typography>
                    </Grid>
                  )}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Positions
                </Typography>
                <Typography variant="body1">
                  {job.positions_filled || 0} / {job.positions_available} filled
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Assign to
                </Typography>
                {(job.owners?.length > 0) ? (
                  <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {job.owners.map((o) => {
                        const role = ownerThru(o).assignment_role === 'primary' ? 'Primary' : 'Secondary';
                        const name =
                          [o.first_name, o.last_name].filter(Boolean).join(' ').trim() || o.email || o.id;
                        return (
                          <Chip key={o.id} size="small" label={`${name} (${role})`} />
                        );
                      })}
                    </Box>
                    {(hasPermission('jobs_modify') || isAdmin) && job.owners.length > 1 ? (
                      <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                        <InputLabel id="job-primary-assignee-label">Primary assignee</InputLabel>
                        <Select
                          labelId="job-primary-assignee-label"
                          label="Primary assignee"
                          value={primaryContactId}
                          disabled={primarySaving}
                          onChange={(e) => handleSetPrimaryAssignee(e.target.value)}
                        >
                          {job.owners.map((o) => (
                            <MenuItem key={o.id} value={o.id}>
                              {[o.first_name, o.last_name].filter(Boolean).join(' ').trim() || o.email || o.id}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Only managers or administrators can change who is primary.
                        </Typography>
                      </FormControl>
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    —
                  </Typography>
                )}
                <RecordAuditMeta
                  creator={job.creator}
                  updater={job.updater}
                  created_at={job.created_at}
                  updated_at={job.updated_at}
                />
              </Box>
            </CardContent>
          </Card>
        }
      >
          <Card>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab label="Description" /><Tab label={`Submissions (${job.submissions?.length || 0})`} /><Tab label="Skills" />
            </Tabs>
            <CardContent>
              {activeTab === 0 && (<Box><Typography variant="h6" gutterBottom>Job Description</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{job.description_external || job.description_internal || 'No description'}</Typography></Box>)}
              {activeTab === 1 && (
                <List>
                  {job.submissions?.length === 0 ? <Typography color="text.secondary" textAlign="center" py={4}>No submissions yet</Typography> :
                    job.submissions?.map(sub => (
                      <ListItem key={sub.id} divider secondaryAction={
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select value={sub.status} onChange={(e) => handleUpdateSubmission(sub.id, e.target.value)}>
                            <MenuItem value="submitted">Submitted</MenuItem><MenuItem value="client_review">Client Review</MenuItem>
                            <MenuItem value="interview_scheduled">Interview Scheduled</MenuItem><MenuItem value="interviewed">Interviewed</MenuItem>
                            <MenuItem value="selected">Selected</MenuItem><MenuItem value="rejected">Rejected</MenuItem>
                            <MenuItem value="offer_made">Offer Made</MenuItem><MenuItem value="joined">Joined</MenuItem>
                          </Select>
                        </FormControl>
                      }>
                        <ListItemAvatar><Avatar>{sub.candidate?.first_name?.[0]}{sub.candidate?.last_name?.[0]}</Avatar></ListItemAvatar>
                        <ListItemText primary={`${sub.candidate?.first_name} ${sub.candidate?.last_name}`} secondary={`${sub.candidate?.email} • Rate: $${sub.submitted_rate || 'N/A'}/hr`} />
                      </ListItem>
                    ))}
                </List>
              )}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Required Skills</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>{job.skills_required?.split(',').map((s, i) => <Chip key={i} label={s.trim()} size="small" />) || 'None'}</Box>
                  <Typography variant="subtitle2" gutterBottom>Preferred Skills</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{job.skills_preferred?.split(',').map((s, i) => <Chip key={i} label={s.trim()} size="small" variant="outlined" />) || 'None'}</Box>
                </Box>
              )}
            </CardContent>
          </Card>
      </DetailTwoColumnGrid>

      <Dialog open={submitDialog} onClose={() => setSubmitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Candidate</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}><InputLabel>Select Candidate</InputLabel>
            <Select value={submitForm.contact_id} label="Select Candidate" onChange={(e) => setSubmitForm({ ...submitForm, contact_id: e.target.value })}>
              {contacts.map(c => <MenuItem key={c.id} value={c.id}>{c.first_name} {c.last_name} - {c.email}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Submitted Rate ($/hr)" type="number" value={submitForm.submitted_rate} onChange={(e) => setSubmitForm({ ...submitForm, submitted_rate: e.target.value })} sx={{ mb: 2 }} />
          <TextField fullWidth label="Internal Notes" multiline rows={3} value={submitForm.internal_notes} onChange={(e) => setSubmitForm({ ...submitForm, internal_notes: e.target.value })} />
        </DialogContent>
        <DialogActions><Button onClick={() => setSubmitDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleSubmitCandidate} disabled={!submitForm.contact_id}>Submit</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default function JobDetails() {
  const { id } = useParams();
  if (!id) return null;
  return <JobDetailsPanel jobId={id} embedded={false} />;
}
